import { lazy, Suspense } from "react";
import { Outlet } from "react-router-dom";

import { AuthGuard } from "src/auth/guard";
import DashboardLayout from "src/layouts/dashboard";

import { LoadingScreen } from "src/components/loading-screen";
import { FederationContextProvider } from "src/context/FederationContext";

// ----------------------------------------------------------------------

// OVERVIEW
const OverviewPage = lazy(() => import("src/pages/dashboard/app"));
const OverviewEcommercePage = lazy(
  () => import("src/pages/dashboard/ecommerce"),
);
const OverviewAnalyticsPage = lazy(
  () => import("src/pages/dashboard/analytics"),
);
const OverviewBankingPage = lazy(() => import("src/pages/dashboard/banking"));
const OverviewFilePage = lazy(() => import("src/pages/dashboard/file"));
const CommunityConnectionsPage = lazy(
  () => import("src/pages/dashboard/community-connections"),
);

//PROFILE
const SportProviderDataPage = lazy(
  () => import("src/pages/dashboard/sportprovider/data"),
);
const SportProviderImagesPage = lazy(
  () => import("src/pages/dashboard/sportprovider/images"),
);

const SportProviderSportsPage = lazy(
  () => import("src/pages/dashboard/sportprovider/sports"),
);
const SportProviderVenuesPage = lazy(
  () => import("src/pages/dashboard/sportprovider/venues"),
);

const SportProviderBillingInfosPage = lazy(
  () => import("src/pages/dashboard/sportprovider/billing-infos"),
);
const SportProviderStaffPage = lazy(
  () => import("src/pages/dashboard/sportprovider/staff"),
);

const SportProviderSettingsPage = lazy(
  () => import("src/pages/dashboard/sportprovider/settings"),
);

const PricingPage = lazy(
  () => import("src/pages/dashboard/settings/pricing/list"),
);
const DiscountsListPage = lazy(
  () => import("src/pages/dashboard/settings/discounts/list"),
);
const AssociationPortalSettingsPage = lazy(
  () => import("src/pages/dashboard/settings/association-portal"),
);

//PHASE
const PhaseListPage = lazy(
  () => import("src/pages/dashboard/settings/venue/list"),
);
const PhaseCreatePage = lazy(
  () => import("src/pages/dashboard/settings/venue/new"),
);
const PhaseEditPage = lazy(
  () => import("src/pages/dashboard/settings/venue/edit"),
);

//TRAINING
const TrainingListPage = lazy(
  () => import("src/pages/dashboard/settings/training/list"),
);
const TrainingCreatePage = lazy(
  () => import("src/pages/dashboard/settings/training/new"),
);
const TrainingEditPage = lazy(
  () => import("src/pages/dashboard/settings/training/edit"),
);

//BLOCKING
const BlockingListPage = lazy(
  () => import("src/pages/dashboard/settings/blocking/list"),
);
const BlockingCreatePage = lazy(
  () => import("src/pages/dashboard/settings/blocking/new"),
);
const BlockingEditPage = lazy(
  () => import("src/pages/dashboard/settings/blocking/edit"),
);

const PassesListPage = lazy(
  () => import("src/pages/dashboard/settings/passes-tickets/pass/list"),
);
const PassCreatePage = lazy(
  () => import("src/pages/dashboard/settings/passes-tickets/pass/new"),
);
const PassEditPage = lazy(
  () => import("src/pages/dashboard/settings/passes-tickets/pass/edit"),
);

const TicketsListPage = lazy(
  () => import("src/pages/dashboard/settings/passes-tickets/ticket/list"),
);
const TicketCreatePage = lazy(
  () => import("src/pages/dashboard/settings/passes-tickets/ticket/new"),
);
const TicketEditPage = lazy(
  () => import("src/pages/dashboard/settings/passes-tickets/ticket/edit"),
);

const SportProviderLiveStreamingPage = lazy(
  () => import("src/pages/dashboard/sportprovider/livestreaming"),
);

// PRODUCT
const ProductDetailsPage = lazy(
  () => import("src/pages/dashboard/product/details"),
);
const ProductListPage = lazy(() => import("src/pages/dashboard/product/list"));
const ProductCreatePage = lazy(() => import("src/pages/dashboard/product/new"));
const ProductEditPage = lazy(() => import("src/pages/dashboard/product/edit"));

// ORDER
const OrderListPage = lazy(() => import("src/pages/dashboard/order/list"));
const OrderDetailsPage = lazy(
  () => import("src/pages/dashboard/order/details"),
);
const ReservationListPage = lazy(
  () => import("src/pages/dashboard/reservations/list"),
);
// INVOICE
const InvoiceListPage = lazy(() => import("src/pages/dashboard/invoice/list"));
const InvoiceDetailsPage = lazy(
  () => import("src/pages/dashboard/invoice/details"),
);
const InvoiceCreatePage = lazy(() => import("src/pages/dashboard/invoice/new"));
const InvoiceEditPage = lazy(() => import("src/pages/dashboard/invoice/edit"));
// USER
const UserProfilePage = lazy(() => import("src/pages/dashboard/user/profile"));
const UserListPage = lazy(() => import("src/pages/dashboard/user/list"));
// BLOG
const BlogPostsPage = lazy(() => import("src/pages/dashboard/post/list"));
const BlogPostPage = lazy(() => import("src/pages/dashboard/post/details"));
const BlogNewPostPage = lazy(() => import("src/pages/dashboard/post/new"));
const BlogEditPostPage = lazy(() => import("src/pages/dashboard/post/edit"));
// JOB
const JobDetailsPage = lazy(() => import("src/pages/dashboard/job/details"));
const JobListPage = lazy(() => import("src/pages/dashboard/job/list"));
const JobCreatePage = lazy(() => import("src/pages/dashboard/job/new"));
const JobEditPage = lazy(() => import("src/pages/dashboard/job/edit"));
// TOUR
const TourDetailsPage = lazy(() => import("src/pages/dashboard/tour/details"));
const TourListPage = lazy(() => import("src/pages/dashboard/tour/list"));
const TourCreatePage = lazy(() => import("src/pages/dashboard/tour/new"));
const TourEditPage = lazy(() => import("src/pages/dashboard/tour/edit"));
// FILE MANAGER
const FileManagerPage = lazy(() => import("src/pages/dashboard/file-manager"));
// APP
const ChatPage = lazy(() => import("src/pages/dashboard/chat"));
const MailPage = lazy(() => import("src/pages/dashboard/mail"));
const CalendarPage = lazy(() => import("src/pages/dashboard/calendar"));
const KanbanPage = lazy(() => import("src/pages/dashboard/kanban"));
// TEST RENDER PAGE BY ROLE
const PermissionDeniedPage = lazy(
  () => import("src/pages/dashboard/permission"),
);
//ADMIN MANAGEMENT
const AdminUserManagementPage = lazy(
  () => import("src/pages/dashboard/adminuser-management"),
);
// BLANK PAGE
const BlankPage = lazy(() => import("src/pages/dashboard/blank"));

//TOURNAMENT ORGANISER PAGES

const OrganiserProviderTournamentsPage = lazy(
  () => import("src/pages/dashboard/tournaments"),
);
const OrganiserProviderRankingsPage = lazy(
  () => import("src/pages/dashboard/rankings"),
);

const OrganiserProviderPlayersPage = lazy(
  () => import("src/pages/dashboard/players-teams/players/list"),
);

const OrganiserProviderTeamsPage = lazy(
  () => import("src/pages/dashboard/players-teams/teams/list"),
);
const AssociationTeamsPage = lazy(() => import("src/sections/federation-team/association"));

const OrganiserProviderDataPage = lazy(
  () => import("src/pages/dashboard/sportprovider/organiser/data"),
);

const FederationIntegrationsPage = lazy(() => import("src/sections/federation-integrations/integrations-view"));
const FederationCommunicationsPage = lazy(() => import("src/sections/federation-communications/communications-view"));
const FederationFinancePage = lazy(() => import("src/sections/federation-finance/finance-view"));
const FederationPlayerPage = lazy(() => import("src/sections/federation-players/player-registration-view"));
const FederationPlayerSettingsPage = lazy(() => import("src/sections/federation-players/player-registration-settings"));
const FederationLicencePage = lazy(() => import("src/sections/federation-licences/licence-admin-view"));
const FederationClubLicencePage = lazy(() => import("src/sections/federation-licences/licence-club-view"));
const FederationMovementPage = lazy(() => import("src/sections/federation-transfers/movement-view"));
const FederationDocumentPage = lazy(() => import("src/sections/federation-documents/document-workspace"));
const FederationMedicalPage = lazy(() => import("src/sections/federation-medical/medical-view"));
const AssociationAdminPage = lazy(
  () => import("src/sections/association/admin"),
);

const FederationAssignmentsPage = lazy(
  () => import("src/pages/dashboard/federation-assignments"),
);
const FederationApprovalsPage = lazy(
  () => import("src/pages/dashboard/federation-approvals"),
);
const FederationOrganisationsPage = lazy(
  () => import("src/pages/dashboard/federation-organisations"),
);
const FederationRulesetsPage = lazy(
  () => import("src/pages/dashboard/federation-rulesets"),
);

const TournamentCreatePage = lazy(
  () => import("src/pages/dashboard/tournament/new"),
);

const TournamentEditPage = lazy(
  () => import("src/pages/dashboard/tournament/edit"),
);
const TournamentApplicationsPage = lazy(
  () => import("src/pages/dashboard/tournament/applications"),
);

const TournamentManagementPage = lazy(
  () => import("src/pages/dashboard/tournament/management"),
);

// ----------------------------------------------------------------------

export const dashboardRoutes = [
  {
    path: "dashboard",
    element: (
      <AuthGuard>
        <FederationContextProvider>
          <DashboardLayout>
            <Suspense fallback={<LoadingScreen />}>
              <Outlet />
            </Suspense>
          </DashboardLayout>
        </FederationContextProvider>
      </AuthGuard>
    ),
    children: [
      { element: <OverviewPage />, index: true },
      {
        path: "association",
        children: [
          { path: "teams", element: <AssociationTeamsPage /> },
          { index: true, element: <AssociationAdminPage /> },
          { path: "members", element: <AssociationAdminPage mode="members" /> },
          { path: "fees", element: <AssociationAdminPage mode="fees" /> },
          { path: "federation-finance", element: <FederationFinancePage mode="association" /> },
          { path: "player-registrations", element: <FederationPlayerPage mode="association" /> },
          { path: "federation-licences", element: <FederationClubLicencePage /> },
          { path: "federation-transfers", element: <FederationMovementPage mode="association" /> },
          { path: "federation-medical-eligibility", element: <FederationMedicalPage mode="association" /> },
          { path: "federations", element: <FederationOrganisationsPage /> },
          { path: "profile", element: <SportProviderDataPage /> },
          { path: "settings", element: <SportProviderSettingsPage /> },
        ],
      },
      { path: "community-connections", element: <CommunityConnectionsPage /> },
      {
        path: "federation-organisations",
        element: <FederationOrganisationsPage />,
      },
      // { path: "settings", element: <SportProviderSettingsPage /> },
      {
        path: "settings",

        children: [
          {
            path: "global",
            children: [{ element: <SportProviderSettingsPage />, index: true }],
          },
          {
            path: "pricing",
            children: [{ element: <PricingPage />, index: true }],
          },
          {
            path: "discounts",
            children: [{ element: <DiscountsListPage />, index: true }],
          },
          {
            path: "venue",
            children: [
              { element: <PhaseListPage />, index: true },
              { path: "new", element: <PhaseCreatePage /> },
              { path: ":id/edit", element: <PhaseEditPage /> },
            ],
          },
          {
            path: "training",
            children: [
              { element: <TrainingListPage />, index: true },
              { path: "new", element: <TrainingCreatePage /> },
              { path: ":id/edit", element: <TrainingEditPage /> },
            ],
          },
          // { path: "course", element: <ProductEditPage /> },
          // { path: "blocking", element: <ProductEditPage /> },
          {
            path: "blocking",
            children: [
              { element: <BlockingListPage />, index: true },
              { path: "new", element: <BlockingCreatePage /> },
              { path: ":id/edit", element: <BlockingEditPage /> },
            ],
          },
          {
            path: "passes-tickets",
            children: [
              {
                path: "passes",
                children: [
                  { element: <PassesListPage />, index: true },
                  { path: "new", element: <PassCreatePage /> },
                  {
                    path: ":id/edit",
                    element: <PassEditPage />,
                  },
                ],
              },
              {
                path: "tickets",
                children: [
                  { element: <TicketsListPage />, index: true },
                  { path: "new", element: <TicketCreatePage /> },
                  {
                    path: ":id/edit",
                    element: <TicketEditPage />,
                  },
                ],
              },
              //{ element: <PassesTicketsListPage />, index: true },
              // { path: "new", element: <PassesTicketsCreatePage /> },
              // { path: ":type/:id/edit", element: <PassesTicketsEditPage /> },
            ],
          },
        ],
      },
      { path: "calendar", element: <CalendarPage /> },
      { path: "finances", element: <OrderListPage /> },
      {
        path: "user",
        children: [
          { element: <UserProfilePage />, index: true },
          { path: "profile", element: <UserProfilePage /> },
          // { path: "cards", element: <UserCardsPage /> },
          { path: "list", element: <UserListPage /> },
          // { path: "new", element: <UserCreatePage /> },
          // { path: ":id/edit", element: <UserEditPage /> },
          // { path: "account", element: <UserAccountPage /> },
        ],
      },

      { path: "chat", element: <ChatPage /> },
      { path: "analytics", element: <OverviewAnalyticsPage /> },
      { path: "payment-management", element: <ReservationListPage /> },
      {
        path: "livestreaming",
        element: <SportProviderLiveStreamingPage />,
      },
      { path: "adminuser-management", element: <AdminUserManagementPage /> },

      {
        path: "profile",
        children: [
          { path: "data", element: <SportProviderDataPage /> },
          { path: "images", element: <SportProviderImagesPage /> },
          { path: "sports", element: <SportProviderSportsPage /> },
          { path: "venues", element: <SportProviderVenuesPage /> },
          { path: "billing-infos", element: <SportProviderBillingInfosPage /> },
          { path: "staff", element: <SportProviderStaffPage /> },
        ],
      },

      // { path: "tournaments", element: <ReservationListPage /> },

      {
        path: "organiser",
        children: [
          {
            path: "federation-assignments",
            element: <FederationAssignmentsPage />,
          },
          {
            path: "federation-approvals",
            element: <FederationApprovalsPage />,
          },
          { path: "federation-rulesets", element: <FederationRulesetsPage /> },
          { path: "federation-finance", element: <FederationFinancePage /> },
          { path: "federation-integrations", element: <FederationIntegrationsPage /> },
          { path: "federation-communications", element: <FederationCommunicationsPage /> },
          { path: "federation-players", element: <FederationPlayerPage mode="federation" /> },
          { path: "federation-licences", element: <FederationLicencePage /> },
          { path: "federation-medical-eligibility", element: <FederationMedicalPage /> },
          { path: "federation-documents", element: <FederationDocumentPage /> },
          { path: "federation-transfers", element: <FederationMovementPage /> },
          { path: "federation-player-registration-settings", element: <FederationPlayerSettingsPage /> },
          {
            path: "tournaments",
            element: <OrganiserProviderTournamentsPage />,
          },
          { path: "rankings", element: <OrganiserProviderRankingsPage /> },
          {
            path: "players-and-teams/players",
            element: <OrganiserProviderPlayersPage />,
          },
          {
            path: "players-and-teams/teams",
            element: <OrganiserProviderTeamsPage />,
          },
          {
            path: "profile",
            children: [
              { path: "data", element: <OrganiserProviderDataPage /> },
            ],
          },
          {
            path: "settings",
            children: [
              {
                path: "global",
                children: [
                  { element: <SportProviderSettingsPage />, index: true },
                ],
              },
              {
                path: "discounts",
                children: [{ element: <DiscountsListPage />, index: true }],
              },
              {
                path: "association-portal",
                element: <AssociationPortalSettingsPage />,
              },
            ],
          },

          {
            path: "tournament",
            children: [
              { element: <OrganiserProviderTournamentsPage />, index: true },
              { path: "list", element: <OrganiserProviderTournamentsPage /> },

              {
                path: ":id/applications",
                element: <TournamentApplicationsPage />,
              },
              { path: "new", element: <TournamentCreatePage /> },
              { path: ":id/edit", element: <TournamentEditPage /> },
              { path: ":id/management", element: <TournamentManagementPage /> },
            ],
          },
          {
            path: "livestreaming",
            element: <SportProviderLiveStreamingPage />,
          },
          { path: "analytics", element: <OverviewAnalyticsPage /> },
        ],
      },

      {
        path: "product",
        children: [
          { element: <ProductListPage />, index: true },
          { path: "list", element: <ProductListPage /> },
          { path: ":id", element: <ProductDetailsPage /> },
          { path: "new", element: <ProductCreatePage /> },
          { path: ":id/edit", element: <ProductEditPage /> },
        ],
      },
      {
        path: "order",
        children: [
          { element: <OrderListPage />, index: true },
          { path: "list", element: <OrderListPage /> },
          { path: ":id", element: <OrderDetailsPage /> },
        ],
      },
      {
        path: "invoice",
        children: [
          { element: <InvoiceListPage />, index: true },
          { path: "list", element: <InvoiceListPage /> },
          { path: ":id", element: <InvoiceDetailsPage /> },
          { path: ":id/edit", element: <InvoiceEditPage /> },
          { path: "new", element: <InvoiceCreatePage /> },
        ],
      },
      {
        path: "post",
        children: [
          { element: <BlogPostsPage />, index: true },
          { path: "list", element: <BlogPostsPage /> },
          { path: ":title", element: <BlogPostPage /> },
          { path: ":title/edit", element: <BlogEditPostPage /> },
          { path: "new", element: <BlogNewPostPage /> },
        ],
      },
      {
        path: "job",
        children: [
          { element: <JobListPage />, index: true },
          { path: "list", element: <JobListPage /> },
          { path: ":id", element: <JobDetailsPage /> },
          { path: "new", element: <JobCreatePage /> },
          { path: ":id/edit", element: <JobEditPage /> },
        ],
      },
      {
        path: "tour",
        children: [
          { element: <TourListPage />, index: true },
          { path: "list", element: <TourListPage /> },
          { path: ":id", element: <TourDetailsPage /> },
          { path: "new", element: <TourCreatePage /> },
          { path: ":id/edit", element: <TourEditPage /> },
        ],
      },
      { path: "file-manager", element: <FileManagerPage /> },
      { path: "mail", element: <MailPage /> },
      { path: "banking", element: <OverviewBankingPage /> },
      { path: "ecommerce", element: <OverviewEcommercePage /> },
      { path: "file", element: <OverviewFilePage /> },
      { path: "kanban", element: <KanbanPage /> },
      { path: "permission", element: <PermissionDeniedPage /> },
      { path: "blank", element: <BlankPage /> },
    ],
  },
];
